import React, { useState, useEffect, useRef } from 'react';
import useApi from '../api';
import EmbedChat from './EmbedChat';

interface AgentWrapperProps {
    agentId: string;
    isDraft?: boolean;
    isProduct?: boolean;
}

const AgentWrapper: React.FC<AgentWrapperProps> = ({ agentId, isDraft = false}) => {
    const [config, setConfig] = useState<any>(null);
    const { post } = useApi();

    const initDataRef = useRef<any>(null);
    const parentUrlRef = useRef<string>('');
    const hasInitializedRef = useRef(false);

    const fetchConfig = async () => {
        try {
            const status = isDraft ? 'draft' : 'live';
            const { data } = await post(`/agents/${agentId}/config?status=${status}`, {
                current_page: parentUrlRef.current,
                lang: initDataRef.current?.lang,
                deviceInfo: initDataRef.current?.deviceInfo
            });
            setConfig(data);
        } catch (error) {
            console.error('Error getting agent config:', error);
        }
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const parentUrlFromQuery = queryParams.get('aimdoc_parent_url');
        if (parentUrlFromQuery) {
            parentUrlRef.current = parentUrlFromQuery;
        }

        let initTimeout: NodeJS.Timeout;

        const handleMessages = (event: MessageEvent) => {
            if (hasInitializedRef.current) return;

            if (event.data.type === 'parentUrl') {
                parentUrlRef.current = event.data.url;
            } else if (event.data.type === 'initializationData') {
                initDataRef.current = event.data.data;
            }

            if (initTimeout) {
                clearTimeout(initTimeout);
            }

            if (!hasInitializedRef.current && (initDataRef.current || parentUrlRef.current)) {
                initTimeout = setTimeout(() => {
                    if (!hasInitializedRef.current) {
                        console.log('Proceeding with initialization with available data');
                        hasInitializedRef.current = true;
                        fetchConfig();
                    }
                }, 500);
            }

            if (!hasInitializedRef.current && initDataRef.current && parentUrlRef.current) {
                clearTimeout(initTimeout);
                hasInitializedRef.current = true;
                fetchConfig();
            }
        };

        window.addEventListener('message', handleMessages);
        
        window.parent.postMessage({ type: 'aimdocReady' }, '*');
        
        initTimeout = setTimeout(() => {
            if (!hasInitializedRef.current) {
                console.log('Initialization timeout reached, proceeding with available data');
                hasInitializedRef.current = true;
                fetchConfig();
            }
        }, 1000);

        return () => {
            window.removeEventListener('message', handleMessages);
            if (initTimeout) {
                clearTimeout(initTimeout);
            }
        };
    }, []);

    if (!config || config.chatbot_enabled === false) {
        return null;
    }

    return <EmbedChat 
        agentId={agentId} 
        isDraft={isDraft} 
        config={config} 
        parentUrl={parentUrlRef.current}
        initData={initDataRef.current}
    />;
};

export default AgentWrapper;